import SendInvitationButton from '@/components/actions/send-invitation/send-invitation-button';
import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar } from '@/components/ui/avatar';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { NativeSelectField, NativeSelectRoot } from '@/components/ui/native-select';
import {
  useBlockConversation,
  useReportConversation,
  useUnblockConversation,
} from '@/hooks/actions/use-conversation-moderation-actions';
import {
  useArchiveConversation,
  useDeleteConversation,
  useUnarchiveConversation,
} from '@/hooks/actions/use-conversation-actions';
import {
  getChronologicalMessages,
  useConversationMessagesQuery,
} from '@/hooks/queries/use-conversation-messages-query';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import type { HTMLChakraProps } from '@chakra-ui/react';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  IconButton,
  Text,
  Textarea,
  VStack,
  chakra,
} from '@chakra-ui/react';
import { CreatorHubReportReason, type Conversation, isCreatorParticipant } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, MoreVert, Trash, UserPlus, WarningTriangle } from 'iconoir-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  canOpenParticipantProfile,
  useOpenParticipantProfile,
} from './use-open-participant-profile';

interface ParticipantHeaderProps extends HTMLChakraProps<'div'> {
  conversation: Conversation;
  currentFilter: 'inbox' | 'archived' | 'sent';
}

type PendingAction = 'archive' | 'unarchive' | 'delete' | 'block' | 'unblock' | null;

const reportReasonOptions = Object.values(CreatorHubReportReason);

const ParticipantHeader = ({ conversation, currentFilter, ...props }: ParticipantHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMessageId, setReportMessageId] = useState('');
  const [reportReason, setReportReason] = useState<string>(CreatorHubReportReason.SPAM);
  const [reportDetails, setReportDetails] = useState('');
  const archive = useArchiveConversation(conversation.brandId);
  const unarchive = useUnarchiveConversation(conversation.brandId);
  const remove = useDeleteConversation(conversation.brandId);
  const block = useBlockConversation(conversation.brandId);
  const unblock = useUnblockConversation(conversation.brandId);
  const report = useReportConversation(conversation.brandId);
  const { data: messagesData } = useConversationMessagesQuery(
    conversation.brandId,
    conversation.id
  );
  const participant = conversation.participant;
  const isCreator = isCreatorParticipant(participant);
  const isArchived = conversation.status === 'archived';
  const isBrandBlocked =
    conversation.moderation.state === 'blocked' &&
    conversation.moderation.reason === 'brand_action';
  const canToggleBrandBlock = conversation.moderation.state === 'clear' || isBrandBlocked;
  const canModerate = !!conversation.creatorIdentityId;
  const viewProfile = useOpenParticipantProfile(participant);
  // Same rule as the list row: the server names every thread, so the header
  // shows that name verbatim and never substitutes a label of its own.
  const displayName = participant?.name ?? '';
  const isActionPending =
    archive.isPending ||
    unarchive.isPending ||
    remove.isPending ||
    block.isPending ||
    unblock.isPending;
  const reportableMessages = useMemo(
    () => getChronologicalMessages(messagesData?.pages).filter((message) => !message.isBrandSender),
    [messagesData?.pages]
  );

  const handleInvitationSent = () => {
    queryClient.invalidateQueries({
      queryKey: conversationQueryKeys.detail(conversation.brandId, conversation.id),
    });
  };

  const handleAction = async () => {
    if (!pendingAction) return;

    const action = pendingAction;
    try {
      if (action === 'archive') await archive.mutateAsync({ conversationId: conversation.id });
      if (action === 'unarchive') await unarchive.mutateAsync({ conversationId: conversation.id });
      if (action === 'delete') await remove.mutateAsync({ conversationId: conversation.id });
      if (action === 'block') await block.mutateAsync({ conversationId: conversation.id });
      if (action === 'unblock') await unblock.mutateAsync({ conversationId: conversation.id });
    } catch {
      return;
    }

    setPendingAction(null);
    if (
      action === 'delete' ||
      (action === 'archive' && currentFilter !== 'archived') ||
      (action === 'unarchive' && currentFilter === 'archived')
    ) {
      queryClient.removeQueries({
        queryKey: conversationQueryKeys.detail(conversation.brandId, conversation.id),
      });
      navigate(`/messages?messageFilter=${currentFilter}`);
    }
  };

  const openReport = () => {
    setReportMessageId(reportableMessages.at(-1)?.id ?? '');
    setReportReason(CreatorHubReportReason.SPAM);
    setReportDetails('');
    setReportOpen(true);
  };

  const closeReport = () => {
    if (!report.isPending) setReportOpen(false);
  };

  const handleReport = async () => {
    if (!reportMessageId) return;
    if (reportReason === CreatorHubReportReason.OTHER && !reportDetails.trim()) return;

    try {
      await report.mutateAsync(
        reportReason === CreatorHubReportReason.OTHER
          ? {
              conversationId: conversation.id,
              messageId: reportMessageId,
              reason: CreatorHubReportReason.OTHER,
              details: reportDetails.trim(),
            }
          : {
              conversationId: conversation.id,
              messageId: reportMessageId,
              reason: reportReason as Exclude<
                (typeof CreatorHubReportReason)[keyof typeof CreatorHubReportReason],
                typeof CreatorHubReportReason.OTHER
              >,
            }
      );
      setReportOpen(false);
    } catch {
      // The mutation's translated toaster describes the failure and preserves the dialog inputs.
    }
  };

  const isModerationAction = pendingAction === 'block' || pendingAction === 'unblock';
  const actionTranslationPrefix = isModerationAction
    ? `messages.moderation.${pendingAction}`
    : `messages.conversationActions.${pendingAction}`;

  return (
    <Box
      px={5}
      py={3}
      display="flex"
      alignItems="center"
      borderBottom="1px solid"
      borderColor="border"
      {...props}
    >
      <Flex align="center" justify="space-between" gap={4} w="full">
        <Flex gap={3} align="center" minW={0}>
          <Avatar
            name={displayName}
            size="md"
            src={participant?.avatarUrl || undefined}
            colorPalette="primary"
          />
          <Box minW={0}>
            <Flex align="center">
              <Heading fontSize="15px" fontWeight="semibold" as="h2" lineHeight="1.25" truncate>
                {displayName}
              </Heading>
              {isCreator && participant.isAffiliate && (
                <Badge colorPalette="green" variant="subtle" ml={2} size="sm" flexShrink={0}>
                  {t('messages.enrolled')}
                </Badge>
              )}
            </Flex>
            {isCreator && participant.socialProfile && (
              <Text fontSize="13px" color="fg.muted" lineHeight="1.3" truncate>
                @{participant.socialProfile.platformUsername}
              </Text>
            )}
          </Box>
        </Flex>
        <Flex align="center" gap={2} flexShrink={0}>
          {isCreator && participant.socialProfileId && (
            <SendInvitationButton
              size="sm"
              variant="outline"
              creatorId={participant.socialProfileId}
              onSuccess={handleInvitationSent}
            >
              <UserPlus width={14} height={14} />
              {t('messages.inviteToProgram')}
            </SendInvitationButton>
          )}
          <MenuRoot>
            <ButtonGroup attached size="sm" variant="outline" colorPalette="gray">
              <Button onClick={viewProfile} disabled={!canOpenParticipantProfile(participant)}>
                {t('messages.seeProfile')}
              </Button>
              <MenuTrigger asChild>
                <IconButton aria-label={t('messages.conversationActions.menu')}>
                  <MoreVert width={18} height={18} />
                </IconButton>
              </MenuTrigger>
            </ButtonGroup>
            <MenuContent>
              <MenuItem
                value={isArchived ? 'unarchive' : 'archive'}
                onClick={() => setPendingAction(isArchived ? 'unarchive' : 'archive')}
                disabled={isActionPending}
              >
                <Archive width={16} height={16} />
                {isArchived
                  ? t('messages.conversationActions.unarchive.label')
                  : t('messages.conversationActions.archive.label')}
              </MenuItem>
              <MenuItem
                value="report"
                onClick={openReport}
                disabled={!canModerate || isActionPending}
              >
                <WarningTriangle width={16} height={16} />
                {t('messages.moderation.report.label')}
              </MenuItem>
              {canToggleBrandBlock && (
                <MenuItem
                  value={isBrandBlocked ? 'unblock' : 'block'}
                  color={isBrandBlocked ? undefined : 'red.600'}
                  onClick={() => setPendingAction(isBrandBlocked ? 'unblock' : 'block')}
                  disabled={!canModerate || isActionPending}
                >
                  <Archive width={16} height={16} />
                  {isBrandBlocked
                    ? t('messages.moderation.unblock.label')
                    : t('messages.moderation.block.label')}
                </MenuItem>
              )}
              <MenuItem
                value="delete"
                color="red.600"
                onClick={() => setPendingAction('delete')}
                disabled={isActionPending}
              >
                <Trash width={16} height={16} />
                {t('messages.conversationActions.delete.label')}
              </MenuItem>
            </MenuContent>
          </MenuRoot>
        </Flex>
      </Flex>
      <ConfirmDialog
        isOpen={pendingAction !== null}
        title={pendingAction ? t(`${actionTranslationPrefix}.title`) : ''}
        description={
          pendingAction
            ? isModerationAction
              ? t(`${actionTranslationPrefix}.description`)
              : t(`${actionTranslationPrefix}.description`, { name: displayName })
            : ''
        }
        confirmText={pendingAction ? t(`${actionTranslationPrefix}.confirm`) : ''}
        confirmColorPalette={
          pendingAction === 'delete' || pendingAction === 'block' ? 'red' : 'gray'
        }
        isLoading={isActionPending}
        onConfirm={handleAction}
        onCancel={() => setPendingAction(null)}
      />
      <DialogRoot
        open={reportOpen}
        onOpenChange={({ open }) => {
          if (!open) closeReport();
        }}
        placement="center"
      >
        <DialogContent maxW="md">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>{t('messages.moderation.report.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {reportableMessages.length === 0 ? (
              <Text color="gray.600">{t('messages.moderation.report.noMessages')}</Text>
            ) : (
              <VStack align="stretch" gap={4}>
                <VStack align="stretch" gap={1}>
                  <chakra.label htmlFor="conversation-report-message" fontWeight="medium">
                    {t('messages.moderation.report.message')}
                  </chakra.label>
                  <NativeSelectRoot>
                    <NativeSelectField
                      id="conversation-report-message"
                      value={reportMessageId}
                      onChange={(event) => setReportMessageId(event.target.value)}
                      items={reportableMessages.map((message) => ({
                        value: message.id,
                        label: message.content || message.id,
                      }))}
                    />
                  </NativeSelectRoot>
                </VStack>
                <VStack align="stretch" gap={1}>
                  <chakra.label htmlFor="conversation-report-reason" fontWeight="medium">
                    {t('messages.moderation.report.reason')}
                  </chakra.label>
                  <NativeSelectRoot>
                    <NativeSelectField
                      id="conversation-report-reason"
                      value={reportReason}
                      onChange={(event) => setReportReason(event.target.value)}
                      items={reportReasonOptions.map((reason) => ({
                        value: reason,
                        label: t(`messages.moderation.report.reasons.${reason}`),
                      }))}
                    />
                  </NativeSelectRoot>
                </VStack>
                {reportReason === CreatorHubReportReason.OTHER && (
                  <VStack align="stretch" gap={1}>
                    <chakra.label htmlFor="conversation-report-details" fontWeight="medium">
                      {t('messages.moderation.report.details')}
                    </chakra.label>
                    <Textarea
                      id="conversation-report-details"
                      value={reportDetails}
                      onChange={(event) => setReportDetails(event.target.value)}
                      placeholder={t('messages.moderation.report.detailsPlaceholder')}
                    />
                  </VStack>
                )}
              </VStack>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={closeReport} disabled={report.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              colorPalette="red"
              onClick={handleReport}
              loading={report.isPending}
              disabled={
                !reportMessageId ||
                (reportReason === CreatorHubReportReason.OTHER && !reportDetails.trim())
              }
            >
              {t('messages.moderation.report.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  );
};

export default ParticipantHeader;
